import { ChevronRight } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { T } from "@/i18n";
import { RouterPaths, RouterSearch } from "@/models";
import { Link, useRouterState } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export interface SidebarItemWithChildren {
  title: string;
  icon: React.ReactNode;
  items: SidebarItem<RouterPaths>[];
}

export interface SidebarItem<T extends RouterPaths> {
  title: string;
  icon: React.ReactNode;
  url: T;
  search?: RouterSearch<T>;
  items?: undefined;
}

export type NavbarItems = Array<
  SidebarItemWithChildren | SidebarItem<RouterPaths>
>;

export function NavMain({ items }: { items: NavbarItems }) {
  const router = useRouterState();
  const { t } = useTranslation();

  const isActive = useCallback(
    (route: string) => {
      return router.matches.some((match) => match.id === route);
    },
    [router],
  );

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) => {
          if (!item.items) {
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild isActive={isActive(item.url)}>
                  <Link to={item.url} search={item.search}>
                    {item.icon} <T tKey={item.title} />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          }
          return (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={item.items.some((si) => isActive(si.url))}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={t(item.title)}>
                    {item.icon}
                    <span>
                      <T tKey={item.title} />
                    </span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild>
                          <a href={subItem.url}>
                            {subItem.icon}
                            <span>
                              <T tKey={subItem.title} />
                            </span>
                          </a>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
